import { Component } from '@theme/component';

/**
 * Drives sections/store-page.liquid: computes the "aberta agora / fecha às Xh"
 * status (and the "Segunda a Sábado: 10h–20h" summary) client-side from the
 * `data-hours` JSON so it reflects the visitor's local time, expands the hours
 * table, copies the address/page link, triggers the share sheet, and runs the
 * photo gallery lightbox.
 */

const DAY_INDEX = {
  domingo: 0,
  segunda: 1,
  terca: 2,
  quarta: 3,
  quinta: 4,
  sexta: 5,
  sabado: 6,
};

/** @param {string} value */
function normalizeDay(value) {
  return value
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replace(/-?\s*feira$/, '')
    .trim();
}

/** @param {string} value */
function parseHoursRange(value) {
  const match = value?.match(/(\d{1,2})h(\d{2})?\s*[–\-]\s*(\d{1,2})h(\d{2})?/);
  if (!match) return null;

  const [, openH, openM = '0', closeH, closeM = '0'] = match;
  return {
    open: Number(openH) * 60 + Number(openM),
    close: Number(closeH) * 60 + Number(closeM),
  };
}

class StorePage extends Component {
  #entries = [];
  #galleryImages = [];
  #galleryIndex = 0;
  #labelResetTimeout;

  connectedCallback() {
    super.connectedCallback();
    this.#entries = this.#parseHours();
    this.#galleryImages = this.#parseGalleryImages();
    this.#renderStatus();
  }

  toggleHours() {
    const panel = this.refs.hoursPanel;
    if (!panel) return;

    const willOpen = panel.hidden;
    panel.hidden = !willOpen;
    this.refs.hoursToggle?.setAttribute('aria-expanded', String(willOpen));
    this.refs.hoursCaret?.classList.toggle('is-open', willOpen);
  }

  async copyAddress() {
    const address = this.dataset.address;
    if (!address) return;

    try {
      await navigator.clipboard.writeText(address);
      this.#flashLabel(this.refs.copyAddressLabel, 'Endereço copiado!');
    } catch (error) {
      console.error(error);
    }
  }

  async copyLink() {
    try {
      await navigator.clipboard.writeText(window.location.href);
      this.#flashLabel(this.refs.copyLinkLabel, 'Link copiado!');
    } catch (error) {
      console.error(error);
    }
  }

  async share() {
    const shareData = {
      title: this.dataset.storeName || document.title,
      url: window.location.href,
    };

    if (navigator.share) {
      try {
        await navigator.share(shareData);
      } catch (error) {
        if (error?.name !== 'AbortError') console.error(error);
      }
      return;
    }

    this.copyLink();
  }

  /** @param {{index: number}} data */
  openGallery(data) {
    if (!this.#galleryImages.length) return;

    this.#galleryIndex = data?.index ?? 0;
    this.#updateLightboxImage();
    if (this.refs.lightbox instanceof HTMLDialogElement) this.refs.lightbox.showModal();
  }

  closeGallery() {
    if (this.refs.lightbox instanceof HTMLDialogElement) this.refs.lightbox.close();
  }

  /** @param {PointerEvent} event */
  closeGalleryOnBackdrop(event) {
    if (event.target === this.refs.lightbox) this.closeGallery();
  }

  /** Stops a click on the image/counter from bubbling to the backdrop-close handler. */
  preventClose() {}

  nextImage() {
    if (!this.#galleryImages.length) return;
    this.#galleryIndex = (this.#galleryIndex + 1) % this.#galleryImages.length;
    this.#updateLightboxImage();
  }

  previousImage() {
    if (!this.#galleryImages.length) return;
    this.#galleryIndex = (this.#galleryIndex - 1 + this.#galleryImages.length) % this.#galleryImages.length;
    this.#updateLightboxImage();
  }

  #parseHours() {
    try {
      /** @type {{day: string, hours: string, closed: boolean}[]} */
      const raw = JSON.parse(this.dataset.hours || '[]');
      return raw
        .filter((entry) => entry.day)
        .map((entry) => ({
          day: entry.day,
          dayIndex: DAY_INDEX[normalizeDay(entry.day)],
          hours: entry.hours || '',
          closed: Boolean(entry.closed),
          range: entry.closed ? null : parseHoursRange(entry.hours),
        }));
    } catch (error) {
      console.error(error);
      return [];
    }
  }

  #parseGalleryImages() {
    try {
      return JSON.parse(this.refs.galleryGrid?.dataset.images || '[]');
    } catch (error) {
      console.error(error);
      return [];
    }
  }

  #renderStatus() {
    if (!this.#entries.length) return;

    const now = new Date();
    const todayIndex = now.getDay();
    const minutesNow = now.getHours() * 60 + now.getMinutes();

    const today = this.#entries.find((entry) => entry.dayIndex === todayIndex);
    const isOpen = Boolean(today && !today.closed && today.range && minutesNow >= today.range.open && minutesNow < today.range.close);

    let statusLabel;
    if (isOpen && today?.range) {
      statusLabel = `Aberta agora · Fecha às ${this.#formatMinutes(today.range.close)}`;
    } else {
      const next = this.#nextOpening(todayIndex, minutesNow);
      statusLabel = next ? `Fechada agora · Abre ${next}` : 'Fechada agora';
    }

    const summary = this.#computeSummary();

    for (const dot of [this.refs.heroStatusDot, this.refs.summaryStatusDot, this.refs.sidebarStatusDot]) {
      dot?.classList.toggle('is-open', isOpen);
      dot?.classList.toggle('is-closed', !isOpen);
    }
    if (this.refs.heroStatusText) this.refs.heroStatusText.textContent = isOpen ? 'Aberta agora' : 'Fechada agora';
    if (this.refs.summaryStatusText) this.refs.summaryStatusText.textContent = statusLabel;
    if (this.refs.summaryBadge) this.refs.summaryBadge.textContent = isOpen ? 'Aberta' : 'Fechada';
    if (this.refs.sidebarStatusText) this.refs.sidebarStatusText.textContent = statusLabel;
    if (this.refs.sidebarHoursSummary) this.refs.sidebarHoursSummary.textContent = summary;

    if (Array.isArray(this.refs.hoursRows)) {
      for (const row of this.refs.hoursRows) {
        const isToday = normalizeDay(row.dataset.day || '') === normalizeDay(today?.day || '');
        row.classList.toggle('is-today', isToday);
      }
    }
  }

  /** @param {number} fromDayIndex @param {number} fromMinutes */
  #nextOpening(fromDayIndex, fromMinutes) {
    const today = this.#entries.find((entry) => entry.dayIndex === fromDayIndex);
    if (today && !today.closed && today.range && fromMinutes < today.range.open) {
      return `às ${this.#formatMinutes(today.range.open)}`;
    }

    for (let offset = 1; offset <= 7; offset++) {
      const dayIndex = (fromDayIndex + offset) % 7;
      const entry = this.#entries.find((e) => e.dayIndex === dayIndex);
      if (entry && !entry.closed && entry.range) {
        const label = offset === 1 ? 'amanhã' : entry.day;
        return `${label} às ${this.#formatMinutes(entry.range.open)}`;
      }
    }

    return '';
  }

  #computeSummary() {
    let best = [];
    let current = [];

    const flush = () => {
      if (current.length > best.length) best = current;
      current = [];
    };

    for (const entry of this.#entries) {
      if (entry.closed || !entry.hours) {
        flush();
        continue;
      }

      const last = current[current.length - 1];
      if (last && last.hours === entry.hours) {
        current.push(entry);
      } else {
        flush();
        current = [entry];
      }
    }
    flush();

    if (!best.length) return '';

    const first = best[0];
    const last = best[best.length - 1];
    return best.length > 1 ? `${first.day} a ${last.day}: ${first.hours}` : `${first.day}: ${first.hours}`;
  }

  /** @param {number} minutes */
  #formatMinutes(minutes) {
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    return mins === 0 ? `${hours}h` : `${hours}h${String(mins).padStart(2, '0')}`;
  }

  #updateLightboxImage() {
    const image = this.#galleryImages[this.#galleryIndex];
    if (!image || !(this.refs.lightboxImage instanceof HTMLImageElement)) return;

    this.refs.lightboxImage.src = image.src;
    this.refs.lightboxImage.alt = image.alt || '';
    if (this.refs.lightboxCounter) {
      this.refs.lightboxCounter.textContent = `${this.#galleryIndex + 1} de ${this.#galleryImages.length}`;
    }
  }

  /** @param {Element | undefined} label @param {string} message */
  #flashLabel(label, message) {
    if (!label) return;

    const original = label.textContent;
    label.textContent = message;
    window.clearTimeout(this.#labelResetTimeout);
    this.#labelResetTimeout = window.setTimeout(() => {
      label.textContent = original;
    }, 2000);
  }
}

customElements.define('store-page-component', StorePage);
